/**
 * @graphql cc
 */
import { useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import type { PagePropertiesReportMacroParams } from '../pprTypes.ts';
import { buildDetailsSummaryVariables } from '../utils/buildDetailsSummaryVariables.ts';

import type {
	useDetailsSummaryADFQueryQuery$data,
	useDetailsSummaryADFQueryQuery$variables,
} from './__generated__/useDetailsSummaryADFQueryQuery.graphql.ts';

export const DetailsSummaryADFQuery = graphql`
	query useDetailsSummaryADFQueryQuery(
		$cql: String
		$detailsId: String
		$sortBy: String
		$reverseSort: Boolean
		$spaceKey: String!
		$contentId: ID!
		$pageSize: Int
		$pageIndex: Int = 0
		$countComments: Boolean
		$countUnresolvedComments: Boolean
		$countLikes: Boolean
		$showPageLabels: Boolean
		$showLastModified: Boolean
		$showCreator: Boolean
		$headings: String
		$contentRepresentation: String = "atlas_doc_format"
		$macroId: String
	) {
		detailsLines(
			cql: $cql
			detailsId: $detailsId
			sortBy: $sortBy
			reverseSort: $reverseSort
			spaceKey: $spaceKey
			contentId: $contentId
			pageSize: $pageSize
			pageIndex: $pageIndex
			countComments: $countComments
			countUnresolvedComments: $countUnresolvedComments
			countLikes: $countLikes
			showPageLabels: $showPageLabels
			showLastModified: $showLastModified
			showCreator: $showCreator
			headings: $headings
			contentRepresentation: $contentRepresentation
			macroId: $macroId
		) {
			currentPage
			totalPages
			renderedHeadings
			macroId
			detailLines {
				id
				title
				relativeLink
				details
				reactionsCount
				commentsCount
				rowId
				macroId
				unresolvedCommentsCount
				labels {
					name
					displayTitle
					urlPath
				}
				creator {
					name
					accountId
				}
				lastModified {
					friendlyModificationDate
					sortableDate
				}
			}
		}
	}
`;

export const useDetailsSummaryADFQuery = ({
	macroDefaultProps,
	macroParams,
}: {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: PagePropertiesReportMacroParams;
}) => {
	const [pageIndex, setPageIndex] = useState(0);
	const { location } = useContext(RoutesContext);

	useEffect(() => {
		setPageIndex(0);
	}, [macroParams]);

	const isPDFExport = location?.pathname.startsWith('/wiki/pdf/');

	const { data, error, loading } = useQueryInterop<
		useDetailsSummaryADFQueryQuery$data,
		useDetailsSummaryADFQueryQuery$variables
	>(DetailsSummaryADFQuery, {
		variables: buildDetailsSummaryVariables({
			macroParams,
			contentId: macroDefaultProps.contentId,
			spaceKey: macroDefaultProps.spaceKey,
			macroId: macroDefaultProps?.attributes?.macroId?.toString() || '',
			pageIndex,
			isPDFExport,
		}),
	});

	const isInitialLoad = loading && !data;

	const paginateTable = (_: any, pageNumber: number): void => {
		setPageIndex(pageNumber - 1);
	};

	return {
		data,
		error,
		loading,
		isInitialLoad,
		isPDFExport,
		pageIndex,
		paginateTable,
		totalPages: data?.detailsLines?.totalPages || 0,
	};
};
